module ContractProfitFadePageView

open FSharpPlus
open Client
open Fable.React.Props
open ContractProfitFadePageModel
open Fulma
open System
open Fable.React
open Model
open PortalLibs.Client.Components
open PortalLibs.Client.Util
open Fable.FontAwesome
open Shared
open Fulma.Extensions.Wikiki
open Fable.Helpers.Moment
open PortalLibs.Client.Components.Dropdown
open PortalLibs.Client.Components.NewTableNext
open Feliz

let toContractProfitFade contractWithProfitFade =
  { Id = contractWithProfitFade.ContractProfitFadeId
    ContractId = contractWithProfitFade.ContractId
    ContractAmount = contractWithProfitFade.ContractAmount
    CurrentGP = contractWithProfitFade.CurrentGP
    GPMinus1 = contractWithProfitFade.GPMinus1
    GPMinus2 = contractWithProfitFade.GPMinus2
    GPMinus3 = contractWithProfitFade.GPMinus3
    GPMinus4 = contractWithProfitFade.GPMinus4
    GPLifeDelta = contractWithProfitFade.GPLifeDelta
    GP3MthDelta = contractWithProfitFade.GP3MthDelta
    ProfitLifeDelta = contractWithProfitFade.ProfitLifeDelta
    Profit3MthDelta = contractWithProfitFade.Profit3MthDelta }


let searchBarLevel left right =
  Level.level [ Level.Level.Props [
                  Style [ MarginBottom "0.5rem" ]
                ] ] [
    Level.left [] [ Level.item [] left ]
    Level.right [] [
      Level.item [ Level.Item.CustomClass "report-search-col" ] right
    ]
  ]

let OnlyOneDropdownSpecific =
  OnlyOneDropdownGeneric<bool, unit, obj, obj>

let searchBar model dispatch =
  let searchViewChangedCallback =
    React.useCallback (SearchboxChanged >> dispatch, [||])

  let activeChangedCallback =
    React.useCallback (ActiveChanged >> dispatch, [||])

  searchBarLevel [ div [ Style [ Width "90px" ] ] [
                     OnlyOneDropdownSpecific
                       {| Title = Name "Active"
                          Items = ItemList [ true; false ]
                          SelectMode =
                           SingleSelect(
                             { UpdateModel = Simple activeChangedCallback
                               SelectedItem = Item model.Active
                               LinkUrl = None }
                           )
                          DisplayValue =
                           (fun m ->
                             match m with
                             | true -> "Yes"
                             | false -> "No")
                          Searchable = false
                          Type = Standard
                          Key = "ContractProfitFadeContractActive" |}
                   ] ] [
    SearchboxNew.SearchboxNew
      {| Value = model.SearchboxValue
         IsLoading = false
         IconVisible = false
         ValueChanged = searchViewChangedCallback
         Key = "ContractProfitFadePageSearch"
         ShouldFocus = false |}
  ]

let formatMonth (month: DateTime) = (month |> moment.Invoke).format "MMM"

let profitFadePartTop percentage color month value =
  let positive = percentage > 0.0
  let absPercentage = Math.Abs(percentage)

  div [ ClassName(Tooltip.ClassName + " " + "has-tooltip-left")
        Tooltip.dataTooltip (sprintf "%s:  %.1f%%" (formatMonth month) value)
        Style [
          Height "100%"
          FlexGrow "1"
          Margin "0 1px"
        ] ] [
    div [ Style [
            Height(
              sprintf
                "%.1f%%"
                (if positive then
                   100.0 - absPercentage + 0.1
                 else
                   100.0)
            )
          ] ] []
    div [ Style [
            yield BorderRadius "6px 6px 0 0"
            yield
              Height(
                if positive then
                  sprintf "%.1f%%" absPercentage
                else
                  "0"
              )
            if positive then
              yield BackgroundColor color
          ] ] []
  ]

let profitFadePartBottom percentage color month value =
  let positive = percentage > 0.0
  let absPercentage = Math.Abs(percentage)

  div [ ClassName(Tooltip.ClassName + " " + "has-tooltip-left")
        Tooltip.dataTooltip (sprintf "%s:  %.1f%%" (formatMonth month) value)
        Style [
          Height "100%"
          FlexGrow "1"
          Margin "0 1px"

          ] ] [
    div [ Style [
            if not positive then
              yield BackgroundColor color
            yield BorderRadius "0 0 6px 6px"
            yield
              Height(
                if not positive then
                  sprintf "%.1f%%" absPercentage
                else
                  "0"
              )
          ] ] []
    div [ Style [
            Height(
              sprintf
                "%.1f%%"
                (if not positive then
                   (100.0 - absPercentage)
                 else
                   100.0)
            )
          ] ] []
  ]

let profitFade (profitFadeOpt: ContractProfitFade option) =
  match profitFadeOpt with
  | None -> str "No profit fade data"
  | Some profitFade ->
    let percentage (min: float) max value =
      if value = 0.0 then
        0.0
      else
        let border =
          if value < 0.0 then
            Math.Abs(min)
          else
            max

        let roundedBorder =
          if border < 15.0 then 15.0
          else if border = 0.0 then 0.0
          else border

        ((value / roundedBorder) * 100.0)

    let values =
      [ profitFade.GPMinus4 * 100.0
        profitFade.GPMinus3 * 100.0
        profitFade.GPMinus2 * 100.0
        profitFade.GPMinus1 * 100.0
        profitFade.CurrentGP * 100.0 ]

    let min =
      values
      |>> fun f -> Math.Round(f, 3)
      |> List.min

    let max =
      values
      |>> fun f -> Math.Round(f, 3)
      |> List.max

    let allPos =
      values
      |> List.exists (fun item -> item < 0.0)
      |> not

    let allNeg =
      values
      |> List.exists (fun item -> item > 0.0)
      |> not

    let topPercentage =
      if allPos then
        0.9
      else if allNeg then
        0.1
      else
        let minRounded = if min > -15.0 then -15.0 else min
        let maxRounded = if max < 15.0 then 15.0 else max
        maxRounded / (Math.Abs(minRounded) + maxRounded)

    let topHeight = 100.0 * topPercentage


    div [ Style [ Height "35px" ] ] [
      div [ Style [
              Height(sprintf "%.1f%%" topHeight)
              Display DisplayOptions.Flex
              ] ] [
        profitFadePartTop
          (percentage min max profitFade.GPMinus4 * 100.0)
          (BusinessLogic.gpPercentColor profitFade.GPMinus4)
          (DateTime.Now.AddMonths(-4))
          (profitFade.GPMinus4 * 100.0)
        profitFadePartTop
          (percentage min max profitFade.GPMinus3 * 100.0)
          (BusinessLogic.gpPercentColor profitFade.GPMinus3)
          (DateTime.Now.AddMonths(-3))
          (profitFade.GPMinus3 * 100.0)
        profitFadePartTop
          (percentage min max profitFade.GPMinus2 * 100.0)
          (BusinessLogic.gpPercentColor profitFade.GPMinus2)
          (DateTime.Now.AddMonths(-2))
          (profitFade.GPMinus2 * 100.0)
        profitFadePartTop
          (percentage min max profitFade.GPMinus1 * 100.0)
          (BusinessLogic.gpPercentColor profitFade.GPMinus1)
          (DateTime.Now.AddMonths(-1))
          (profitFade.GPMinus1 * 100.0)
        profitFadePartTop
          (percentage min max profitFade.CurrentGP * 100.0)
          (BusinessLogic.gpPercentColor profitFade.CurrentGP)
          DateTime.Now
          (profitFade.CurrentGP * 100.0)
      ]
      div [ Style [
              Height(sprintf "%.1f%%" (100.0 - topHeight))
              Display DisplayOptions.Flex
            ] ] [
        profitFadePartBottom
          (percentage min max profitFade.GPMinus4 * 100.0)
          (BusinessLogic.gpPercentColor profitFade.GPMinus4)
          (DateTime.Now.AddMonths(-4))
          (profitFade.GPMinus4 * 100.0)
        profitFadePartBottom
          (percentage min max profitFade.GPMinus3 * 100.0)
          (BusinessLogic.gpPercentColor profitFade.GPMinus3)
          (DateTime.Now.AddMonths(-3))
          (profitFade.GPMinus3 * 100.0)
        profitFadePartBottom
          (percentage min max profitFade.GPMinus2 * 100.0)
          (BusinessLogic.gpPercentColor profitFade.GPMinus2)
          (DateTime.Now.AddMonths(-2))
          (profitFade.GPMinus2 * 100.0)
        profitFadePartBottom
          (percentage min max profitFade.GPMinus1 * 100.0)
          (BusinessLogic.gpPercentColor profitFade.GPMinus1)
          (DateTime.Now.AddMonths(-1))
          (profitFade.GPMinus1 * 100.0)
        profitFadePartBottom
          (percentage min max profitFade.CurrentGP * 100.0)
          (BusinessLogic.gpPercentColor profitFade.CurrentGP)
          DateTime.Now
          (profitFade.CurrentGP * 100.0)
      ]
    ]

let ProfitFadeGraph =
  React.memo (
    "ProfitFadeGraph",
    (fun (props: {| contractWithProfitFade: ContractWithProfitFade
                    profitFades: Map<ContractId, ContractProfit list>
                    Key: string |}) ->
      tr [ Style [] ] [
        td [ ColSpan 8
             Style [
               TextAlign TextAlignOptions.Center
               Padding "30px 0"
             ] ] [
          loadingMessageOrContent
           "Loading Data..."
            (fun jobProfits ->
              div [] [
                ContractProfitFade.profitFadeValues (
                  toContractProfitFade props.contractWithProfitFade
                  |> Some
                )

                ContractProfitFade.ProfitFadeChart
                  {| profitFadeEntries = jobProfits
                     Key = $"FadeChart-%i{props.contractWithProfitFade.ContractId}" |}
              ])
            (props.profitFades
            |> Map.tryFind props.contractWithProfitFade.ContractId)
        ]
      ]),
    (fun (props: {| contractWithProfitFade: ContractWithProfitFade
                    profitFades: Map<ContractId, ContractProfit list>
                    Key: string |}) -> $"ProfitFadeGraph-%s{props.Key}")
  )

let NewTableNextSpecific =
  NewTableNextGeneric<ContractWithProfitFade, ContractWithProfitFadeField, unit>

let ContractProfitFadeTable =
  React.memo (
    "ContractProfitFadeTable",
    (fun (props: {| tableState: NewTableNext.Model<ContractWithProfitFade, ContractWithProfitFadeField, unit>
                    expandedContractProfitFades: ContractId list
                    profitFades: Map<ContractId, ContractProfit list>
                    contracts: ContractWithProfitFade list
                    dispatch: Msg -> unit |}) ->
      let columns =
        [ { Header = Text "Contract"
            Id = Contract
            Align = Right
            Value = ValueType.Text(fun (fade: ContractWithProfitFade) -> fade.Contract |> Option.defaultValue "")
            Sortable = ValueOrExternal
            Draggable = false
            MinWidth = None
            Footer = None
            InitiallyVisible = true }
          { Header = Text "Name"
            Id = ContractWithProfitFadeField.Name
            Align = Left
            Value =
              ValueType.Custom (fun (fade: ContractWithProfitFade) ->
                let expanded =
                  props.expandedContractProfitFades
                  |> List.contains fade.ContractId

                div [ Style [
                        Display DisplayOptions.Flex
                        JustifyContent "space-between"
                        Width "100%"
                        AlignItems AlignItemsOptions.Center
                      ] ] [
                  a [ OnClick goToUrl
                      Href(Pages.contractUrl fade.ContractId) ] [
                    fade.Name |> emptyStrTagIfNone
                  ]
                  Icon.icon [ Icon.Props [ Style [ Color "#b5b5b5" ] ] ] [
                    Fa.i [ if expanded then
                             yield Fa.Solid.ChevronUp
                           else
                             yield Fa.Solid.ChevronDown ] []
                  ]
                ])
            Sortable = ValueOrExternal
            Draggable = false
            MinWidth = None
            Footer = None
            InitiallyVisible = true }
          { Header = Text "Profit History"
            Id = ContractWithProfitFadeField.ProfitHistory
            Align = Center
            Value =
              ValueType.Custom (fun (fade: ContractWithProfitFade) ->
                div [ Style [
                        Width "100%"
                        Padding "3px 5px"
                      ] ] [
                  profitFade (fade |> toContractProfitFade |> Some)
                ])
            Sortable = ValueOrExternal
            Draggable = false
            MinWidth = Some 170
            Footer = None
            InitiallyVisible = true }
          { Header = Text "GP %"
            Id = CurrentGP
            Align = Right
            Value =
              ValueType.Custom (fun (fade: ContractWithProfitFade) ->
                div [ Style [
                        FlexGrow 1
                        TextAlign TextAlignOptions.Right
                        Padding "0.5em 0.75em"
                      ] ] [
                  strong [ Style [
                             Color(BusinessLogic.gpPercentColor fade.CurrentGP)
                           ] ] [
                    sprintf "%.1f%%" (fade.CurrentGP * 100.0) |> str
                  ]
                ])
            Sortable = ValueOrExternal
            Draggable = false
            MinWidth = Some 80
            Footer = None
            InitiallyVisible = true }
          { Header = Text "Contract Amount"
            Id = ContractAmount
            Align = Right
            Value =
              ValueType.Text (fun (fade: ContractWithProfitFade) ->
                fade.ContractAmount
                |> DisplayNumber.AsCurrency.std)
            Sortable = ValueOrExternal
            Draggable = false
            MinWidth = Some 50
            Footer = None
            InitiallyVisible = true }
          { Header = Text "Profit delta last 3 months"
            Id = Profit3MthDelta
            Align = Right
            Value =
              ValueType.Text (fun (fade: ContractWithProfitFade) ->
                fade.Profit3MthDelta
                |> DisplayNumber.AsCurrency.std)
            Sortable = ValueOrExternal
            Draggable = false
            MinWidth = Some 140
            Footer = None
            InitiallyVisible = true } ]

      div [ ClassName "profit-fade-page-table"
            Style [ FontSize "0.9rem" ] ] [
        NewTableNextSpecific
          {| Config =
              { Model = props.tableState
                Columns = columns
                Items = props.contracts
                ItemKey = fun item -> item.ContractId.ToString()
                SubRows =
                  Some (fun item ->
                    let expanded =
                      props.expandedContractProfitFades
                      |> List.contains item.ContractId

                    [ if expanded then
                        ProfitFadeGraph
                          {| contractWithProfitFade = item
                             profitFades = props.profitFades
                             Key = $"ProfitGraphRow-%i{item.ContractId}" |} ])
                ColumnSelection = false
                Selection = [], false
                Paging =
                  Paged
                    { HidePaginationUnder1Page = true
                      PageSizeOptions = None }
                Key = "ContractProfitFadePageTable"
                Dispatch = TableStateChanged >> props.dispatch
                DataLoad = OnDemand
                StickyHeader = None
                RowClick =
                  Some (fun fade ->
                    ContractProfitFadeExpansionToggled fade.ContractId
                    |> props.dispatch) } |}
      ])
  )

let content model dispatch (contracts: ContractWithProfitFade list) =
  div [ ClassName "profit-fade-page" ] [
    searchBar model dispatch
    ContractProfitFadeTable
      {| tableState = model.TableState
         expandedContractProfitFades = model.ExpandedContractProfitFades
         profitFades = model.ContractProfitFades
         contracts = contracts
         dispatch = dispatch |}
  ]

let ContractProfitFadePageView =
  React.memo (
    "ContractProfitFadePageView",
    fun (props: {| model: ContractProfitFadePageModel.Model
                   dispatch: Msg -> unit |}) ->
      div [ ClassName "profit-fade-page"
            Style [
              MarginTop "25px"
              MaxWidth "1200px"
              MarginLeft "auto"
              MarginRight "auto"
              PaddingBottom "10px"
            ] ] [
        loadingMessageBoxOrContent "Loading Report..." (content props.model props.dispatch) props.model.Contracts
      ]
  )
